import { useConciergeStore } from '@/stores/useConciergeStore'
import type { ChatMode } from '@/stores/useConciergeStore'

/**
 * Sticky banner at the top of the messages area when the chat is in a
 * non-default workflow mode. Tells the user which "room" they're in and
 * gives them a one-click way out.
 *
 * Visual contract: occupies the full content width above the messages,
 * uses a per-mode accent color, never overlays the messages (it's a row
 * above them, not a floating modal). When `chatMode === 'default'` the
 * component renders nothing — zero footprint on the regular chat.
 */
export function ConciergeModeBanner() {
  const chatMode = useConciergeStore((s) => s.chatMode)
  const modeContext = useConciergeStore((s) => s.modeContext)
  const exitMode = useConciergeStore((s) => s.exitMode)

  if (chatMode === 'default') return null

  const accent = MODE_ACCENT[chatMode]
  const label = MODE_LABEL[chatMode]
  const subtitle = renderSubtitle(chatMode, modeContext)
  const secondLine = modeContext?.displaySubtitle || ''

  return (
    <div
      className="concierge-mode-banner"
      style={{
        // CSS variables let concierge.css handle the visual treatment;
        // the per-mode tint stays inline so we can drive it from one
        // place (this file) rather than fragmenting it across CSS.
        ['--mode-accent' as string]: accent,
      }}
    >
      <div className="concierge-mode-banner-text">
        <div>
          <span className="concierge-mode-banner-label">{label}</span>
          {subtitle && (
            <span className="concierge-mode-banner-subtitle"> · {subtitle}</span>
          )}
        </div>
        {secondLine && (
          <div className="concierge-mode-banner-secondline">{secondLine}</div>
        )}
      </div>
      <button
        type="button"
        className="concierge-mode-banner-close"
        onClick={exitMode}
        aria-label="Exit workflow"
        title="Exit workflow (Esc)"
      >
        ×
      </button>
    </div>
  )
}

/** Per-mode accent color. Stays muted — the chat below is the content. */
const MODE_ACCENT: Record<Exclude<ChatMode, 'default'>, string> = {
  wrapup: 'rgb(218, 165, 32)', // amber — "closing the loop"
  prep: 'rgb(100, 149, 237)', // cornflower — "looking ahead"
  review: 'rgb(143, 188, 143)', // sage — "looking back"
  // PENN brand purple — see [[penn_brand_design_standards]]. Distinct
  // from the sage Review accent so the user always knows which review
  // surface they're in.
  penn_review: 'rgb(125, 95, 200)',
}

const MODE_LABEL: Record<Exclude<ChatMode, 'default'>, string> = {
  wrapup: 'Wrap-up',
  prep: 'Prep',
  review: 'Review',
  penn_review: 'Penn Review',
}

function renderSubtitle(
  mode: Exclude<ChatMode, 'default'>,
  ctx: ReturnType<typeof useConciergeStore.getState>['modeContext']
): string {
  if (!ctx) return ''
  if (ctx.displayTitle) return ctx.displayTitle
  if (mode === 'review' && ctx.reviewVariant) {
    return ctx.reviewVariant.replace('_', ' ')
  }
  if (mode === 'penn_review' && ctx.dateFrom && ctx.dateTo) {
    return `${ctx.dateFrom} → ${ctx.dateTo}`
  }
  if (ctx.blockPath) {
    return basename(ctx.blockPath)
  }
  if (ctx.projectPath) {
    return basename(ctx.projectPath)
  }
  return ''
}

function basename(path: string): string {
  const parts = path.split('/').filter(Boolean)
  return parts[parts.length - 1] ?? path
}
